import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Box,
  GitCompare,
  Thermometer,
  Sun,
  TreePine,
  Siren,
  Hammer,
  LogOut,
  RefreshCw,
} from 'lucide-react';
import { useTerritoryStore } from '@/stores/territoryStore';
import { useAuthStore } from '@/stores/authStore';
import { useIngestion } from '@/hooks/useIngestion';

/** Formate une date ISO en date courte FR (ex. « 28 juin 2026 »). */
function formatFrDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

interface NavItem {
  icon: React.ElementType;
  label: string;
  to: string;
}

const NAV_GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Pilotage',
    items: [
      { icon: LayoutDashboard, label: 'Tableau de bord', to: '/' },
      { icon: Box, label: 'Vue 3D', to: '/3d' },
      { icon: GitCompare, label: 'Comparaison', to: '/compare' },
    ],
  },
  {
    title: 'Analyses & simulations',
    items: [
      { icon: Thermometer, label: 'Îlots de chaleur', to: '/heat' },
      { icon: Sun, label: 'Cadastre solaire', to: '/solar' },
      { icon: TreePine, label: 'Suivi ZAN', to: '/zan' },
      { icon: Siren, label: 'Plan Canicule', to: '/canicule' },
      { icon: Hammer, label: 'Rénovation énergétique', to: '/renovation' },
    ],
  },
];

function SidebarNavItem({ item }: { item: NavItem }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isActive =
    item.to === '/' ? pathname === '/' : pathname.startsWith(item.to);

  return (
    <button
      type="button"
      onClick={() => navigate(item.to)}
      className={`relative flex w-full items-center gap-3 rounded-md px-4 py-2.5 text-[13px] font-medium transition-colors ${
        isActive
          ? 'text-white'
          : 'text-[#aeb6d4] hover:bg-white/[0.06] hover:text-white'
      }`}
      style={isActive ? { background: 'rgba(255,255,255,.09)' } : undefined}
    >
      {/* Active left accent bar */}
      {isActive && (
        <span
          aria-hidden="true"
          className="absolute inset-y-[5px] left-0 w-[3px] rounded-r-[2px] bg-primary-500"
        />
      )}

      <item.icon
        size={16}
        className="flex-shrink-0"
        style={isActive ? { color: '#7fa6ff' } : undefined}
      />
      <span>{item.label}</span>
    </button>
  );
}

export function Sidebar() {
  const { activeTerritory } = useTerritoryStore();
  const { logout } = useAuthStore();
  const navigate = useNavigate();

  const insee = activeTerritory?.code_insee ?? null;
  const { isActive: ingestActive, start, isStarting } = useIngestion(insee);
  const refreshing = ingestActive || isStarting;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside
      className="flex w-60 flex-shrink-0 flex-col overflow-hidden"
      style={{ background: 'linear-gradient(180deg,#161d3c,#11162f)' }}
    >
      {/* ── Navigation groups ── */}
      <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-5">
        {NAV_GROUPS.map((group) => (
          <div key={group.title}>
            <p className="mb-1.5 px-4 font-sans text-[10px] font-semibold uppercase tracking-widest text-[#aeb6d4]/60">
              {group.title}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <SidebarNavItem key={item.to} item={item} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* ── Territory card ── */}
      <div className="mx-3 mb-2 rounded-[14px] border border-white/10 bg-white/[0.07] px-3 py-2.5">
        <p className="mb-1 font-sans text-[10px] font-semibold uppercase tracking-widest text-[#aeb6d4]/60">
          Territoire actif
        </p>
        {activeTerritory ? (
          <>
            <p className="font-sans text-[13px] font-semibold leading-snug text-white">
              {activeTerritory.name}
            </p>
            <p className="font-mono text-[10px] text-[#aeb6d4]">
              INSEE&nbsp;{activeTerritory.code_insee}
            </p>
            {activeTerritory.population !== null && (
              <p className="mt-0.5 font-sans text-[11px] text-[#aeb6d4]">
                {activeTerritory.population.toLocaleString('fr-FR')}&nbsp;hab.
              </p>
            )}

            {/* Fraîcheur des données + bouton Rafraîchir (si données complètes) */}
            {activeTerritory.has_full_data !== false &&
              activeTerritory.last_full_ingest_at && (
                <div className="mt-2 border-t border-white/10 pt-2">
                  <p className="font-sans text-[10px] leading-snug text-[#aeb6d4]">
                    Données : BDNB {activeTerritory.data_millesime ?? '—'} · chargé le{' '}
                    {formatFrDate(activeTerritory.last_full_ingest_at)}
                  </p>
                  <button
                    type="button"
                    onClick={() => insee && start(insee)}
                    disabled={refreshing}
                    className="mt-1.5 flex items-center gap-1.5 rounded-md px-1.5 py-1 font-sans text-[11px] font-medium text-[#aeb6d4] transition-colors hover:bg-white/10 hover:text-white disabled:opacity-60"
                  >
                    <RefreshCw
                      size={12}
                      className={`flex-shrink-0 ${refreshing ? 'animate-spin' : ''}`}
                    />
                    <span>{refreshing ? 'Rafraîchissement…' : 'Rafraîchir'}</span>
                  </button>
                </div>
              )}
          </>
        ) : (
          <p className="font-sans text-[12px] text-[#aeb6d4]/60">
            Sélectionnez un territoire
          </p>
        )}
      </div>

      {/* ── Logout ── */}
      <div className="px-3 pb-4">
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-2 rounded-md px-4 py-2.5 font-sans text-sm text-[#aeb6d4] transition-colors hover:bg-white/10 hover:text-white"
        >
          <LogOut size={15} className="flex-shrink-0" />
          <span>Déconnexion</span>
        </button>
      </div>
    </aside>
  );
}
